import { z } from 'zod';
import { emailValidation, nameValidation } from '../../../validation';

// The employer-side contact for an Aviva member (PPL-2639). The case manager is the insurer-side
// contact; this is the contact at the member's employer, captured so the EBP can email them later.
// All fields are optional: a blank input is treated as "not provided" so it validates cleanly and
// is dropped from the stored referral (Firestore ignoreUndefinedProperties). A non-empty value is
// validated like the case manager's equivalent field.
const blankToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

export const ReferralAvivaEmployerContactSchema = z
  .object({
    firstname: z.preprocess(blankToUndefined, nameValidation().optional()),
    lastname: z.preprocess(blankToUndefined, nameValidation().optional()),
    email: z.preprocess(blankToUndefined, emailValidation().optional()),
  })
  .optional()
  // Collapse a contact with no filled fields to undefined so we never store an empty object.
  .transform((contact) =>
    contact && !contact.firstname && !contact.lastname && !contact.email
      ? undefined
      : contact,
  );

export const ReferralAvivaEmployerSchema = z
  .object({
    contact: ReferralAvivaEmployerContactSchema,
  })
  .optional()
  // With no contact, there is no employer to store.
  .transform((employer) =>
    employer && !employer.contact ? undefined : employer,
  );

export type ReferralAvivaEmployerContact = z.infer<
  typeof ReferralAvivaEmployerContactSchema
>;
export type ReferralAvivaEmployer = z.infer<typeof ReferralAvivaEmployerSchema>;
